import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { AuthorityBadge } from "@/components/authority-badge";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import type {
  AuthorityLevel,
  GovernanceRef,
  ProjectContext,
} from "@iamu/shared";
import { AuthorityDialog } from "./context-entry-dialogs";

const COMMON_AUTHORITIES: Array<{
  name: string;
  authorityLevel: AuthorityLevel;
}> = [
  {
    name: "Skatteverket (Swedish Tax Agency Rules)",
    authorityLevel: "regulation",
  },
  {
    name: "GDPR (General Data Protection Regulation)",
    authorityLevel: "legislation",
  },
  {
    name: "WCAG 2.2 AA (Accessibility)",
    authorityLevel: "standard",
  },
  {
    name: "ISO/IEC 27001 (Information Security)",
    authorityLevel: "standard",
  },
];

interface GovernanceSectionProps {
  context: ProjectContext;
  onUpdate: (field: string, value: unknown) => void;
  canEdit: boolean;
}

export function GovernanceSection({
  context,
  onUpdate,
  canEdit,
}: GovernanceSectionProps) {
  const refs = context.governedBy ?? [];
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  function handleSaveRef(ref: GovernanceRef) {
    const updated =
      editingIndex !== null
        ? refs.map((r, i) => (i === editingIndex ? ref : r))
        : [...refs, ref];
    onUpdate("governedBy", updated);
    setIsDialogOpen(false);
    setEditingIndex(null);
  }

  function handleAddPreset(preset: (typeof COMMON_AUTHORITIES)[number]) {
    if (refs.some((r) => r.name.toLowerCase() === preset.name.toLowerCase())) {
      toast.info("Authority already added");
      return;
    }
    const updated = [...refs, preset];
    onUpdate("governedBy", updated);
    toast.success(`Added ${preset.name.split(" (")[0]}`);
  }

  function removeRef(index: number) {
    onUpdate(
      "governedBy",
      refs.filter((_, i) => i !== index),
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">
              Governing authorities & regulations
            </CardTitle>
          </div>
          <CardDescription>
            Laws, statutory regulations, standards, and internal policies that
            constrain your domain.
          </CardDescription>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setEditingIndex(null);
              setIsDialogOpen(true);
            }}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add authority
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex-1">
        {refs.length === 0 ? (
          <div className="py-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              No governing authorities defined yet.
            </p>
            {canEdit && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Quick-add common authorities & standards:
                </p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {COMMON_AUTHORITIES.map((preset) => (
                    <Button
                      key={preset.name}
                      variant="outline"
                      size="xs"
                      onClick={() => handleAddPreset(preset)}
                    >
                      <Plus className="h-3 w-3" />
                      {preset.name.split(" (")[0]}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {refs.map((ref, i) => (
              <div
                key={`${ref.name}-${i}`}
                className="flex items-center justify-between gap-3 py-2.5"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    {ref.name}
                  </p>
                  <div>
                    <AuthorityBadge level={ref.authorityLevel} />
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingIndex(i);
                        setIsDialogOpen(true);
                      }}
                      aria-label={`Edit authority ${ref.name}`}
                    >
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                    <SimpleTooltip
                      content={`Remove authority ${ref.name || i + 1}`}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRef(i)}
                        aria-label={`Remove authority ${ref.name || i + 1}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </SimpleTooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {canEdit && isDialogOpen && (
        <AuthorityDialog
          initialRef={editingIndex !== null ? refs[editingIndex] : undefined}
          onSave={handleSaveRef}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingIndex(null);
          }}
        />
      )}
    </Card>
  );
}
